/**
 * TradingCard — the reusable, rarity-tiered, gold-foil card frame (BL-59 ⭐).
 *
 * The unified card-system's shared language: the enemy reveal (BL-59) presents a
 * threat as a **scanned THREAT DOSSIER** card, and the skill cards (BL-60) will
 * adopt the SAME frame as **Trainer cards** (`kind: "skill"`). It deliberately is
 * NOT a Pokémon clone — our identity is a void+neon **SENTINEL manifest**: VT323
 * terminal type, scanlines + a vertical scan-sweep (the malware is being
 * *analysed*), refined **circuit-etch** accents (`UIMaterial.drawCircuitEtch` —
 * NOT the demoted ⌐ brackets, NOT ornate gold filigree), and a classification
 * stamp. The **gold-foil legendary** is our data-gold / CLASS-Ω (`#FFC23D`, the
 * signed `RARITY.legendary`), not warm Pokémon-gold-ornate.
 *
 * The frame reads premium through layered engine MATERIAL, not a picture in a
 * box (the owner's "Liebe zum Detail"), and **escalates by rarity**:
 *   - an engraved multi-layer **frame** — the real `drawLitPanel` bevelled lit
 *     edge + circuit-etch corners + a treated banner; LEGENDARY adds a real
 *     **gold-foil specular sweep** (the moving catch-light).
 *   - a living **foil** read — CSS scanlines + a scan-sweep + twinkling glints
 *     (the hero's radial-ray field + data-motes live in the `EnemyHolo` art).
 *   - **typographic craft** — a treated display name (outlined/depth on
 *     legendary), a class chip, clean leaded stat/effect blocks.
 *   - **collectible marks** — a faction sigil, a dossier number, rarity pips, an
 *     "illus." wink.
 *   - **tilt** — the BL-57 CSS-3D pivot toward the cursor (the "turn it in the
 *     light" feel), with an opposite parallax on the art.
 * BASIC = clean + restrained (lit edge only); the ramp *is* the premium signal.
 *
 * The lit material + etch + gold-foil are drawn onto a per-card `<canvas>` behind
 * crisp DOM text (never a bloom source) and routed through the ONE shared
 * `UIBloom` (`getSharedUIBloom()`). WebGL absent (headless / BL-44) → the raw
 * `drawLitPanel` material (still `glowCache`-lit) shows; the CSS lit-edge is the
 * final fallback. Every animation is `isReducedMotion()`-gated: reduced → a
 * static-but-rich card (lit frame + etch + scanlines, no sweep / glint / tilt).
 *
 * Anatomy slots (both kinds): **stamp** (classification + class badge) · **art
 * window** (the holo, mounted via `mountHolo`) · **codename** (name + subtitle) ·
 * **readout** (stats + weakness/resistance/immunities + the ability block) ·
 * **footer** (faction sigil + dossier number + rarity pips + a flavour line).
 */
import { drawLitPanel, drawCircuitEtch } from "./UIMaterial.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { deriveRamp } from "../rendering/EmissiveMaterial.ts";
import { rarityMaterial, type RarityName } from "../core/palette.ts";
import { getSharedUIBloom } from "./ProBar.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import {
  UI_TYPE,
  UI_SPACE,
  UI_EASE,
  UI_DURATION,
  UI_FONT_STACK,
  UI_FOIL,
  roleFontSize,
  withUiScale,
} from "./uiTokens.ts";
import { COLORS } from "../core/constants.ts";
import { t } from "../utils/i18n.ts";

export type TradingCardKind = "enemy" | "skill";
/** The rarity ladder the unified card-system reads (display names). */
export type TradingCardRarity =
  | "basic"
  | "rare"
  | "epic"
  | "legendary"
  | "secret";

/** Map a card-system rarity onto the central `RARITY` material tier (the colour
 *  + the §3 lit-edge/bloom escalation). SECRET borrows the epic material weight
 *  but carries its own cloaked stamp/treatment. No new hex — colour stays
 *  central in `core/palette.ts`. */
const RARITY_MATERIAL: Record<TradingCardRarity, RarityName> = {
  basic: "common",
  rare: "rare",
  epic: "epic",
  legendary: "legendary",
  secret: "epic",
};

const RARITY_RANK: Record<TradingCardRarity, number> = {
  basic: 0,
  rare: 1,
  epic: 2,
  secret: 2,
  legendary: 3,
};

export interface TradingCardStat {
  k: string;
  v: string;
  /** `hot` = the threat red, `gold` = bounty/legendary gold, else default. */
  tone?: "hot" | "gold" | "normal";
}

export interface TradingCardContent {
  kind: TradingCardKind;
  rarity: TradingCardRarity;
  /** The subject's identity colour (enemy threat hue / skill element hue). */
  colour: string;
  /** Stamp left — the classification (e.g. "⚠ THREAT MANIFEST"). */
  classification: string;
  /** Stamp right — the class badge (e.g. "CLASS-Ω" / "CLASS-III"). */
  classBadge: string;
  /** The codename (display role headline). */
  name: string;
  /** The subtitle (class / role line). */
  subtitle: string;
  /** The dossier readout rows (HP / SPD / BTY …). */
  stats?: TradingCardStat[];
  weakness?: string;
  resistance?: string;
  immunities?: string;
  /** The "attack" / effect block — the special as the ability. */
  ability?: { title: string; desc: string };
  /** Footer left — the faction sigil + dossier number (e.g. "SENTINEL-7 · BRK 05/10"). */
  footerLeft?: string;
  /** A flavour line (the wink that makes it feel like a real collectible). */
  flavour?: string;
  /** Rarity pips (filled stars). Clamped 1..5. */
  pips?: number;

  // ── skill-aspect (kind:"skill") — the BL-60 Trainer-card slots ──────────────
  /** The category "type" badge (Trainer-card type — ◆ OFFENSIVE / ◇ DEFENSIVE /
   *  ⬡ UTILITY / ★ LEGENDARY). Stamped in the banner for skills, our
   *  Supporter/Item/Tool/Stadium. `cat` keys the per-category tint class. */
  typeBadge?: { symbol: string; label: string; cat: string };
  /** The effect block — `headline` becomes the treated move-name, `description`
   *  the leaded effect text. */
  effect?: { headline?: string; description: string };
  /** The persistence chip (PERMANENT / {N}W / PAYLOAD / INSTANT). */
  persistence?: { label: string; kind: string };
}

/** How far the lit frame canvas overscans the card box so the edge glow / foil
 *  sweep / bloom flare bleeds out instead of clipping at the border. */
const OVERSCAN = 18;

/** The signed legendary gold — referenced for the CSS rungs only (the same
 *  `RARITY.legendary.mid`, kept literal here so the stylesheet is self-contained;
 *  NOT a new gameplay hex). S139 Glacier: re-toned to the new legendary mid. */
const RARITY_GOLD = "#E8C098";

export class TradingCard {
  /** The card root — crisp DOM text on top of the lit frame canvas. */
  readonly el: HTMLDivElement;
  readonly kind: TradingCardKind;
  readonly rarity: TradingCardRarity;
  private readonly material: RarityName;
  private readonly rank: number;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly artHost: HTMLDivElement;
  private readonly nameEl: HTMLDivElement;
  private readonly nameText: string;
  private raf = 0;
  private decodeRaf = 0;
  private resizeObs: ResizeObserver | null = null;
  private destroyed = false;

  constructor(content: TradingCardContent) {
    TradingCard.ensureStyles();
    this.kind = content.kind;
    this.rarity = content.rarity;
    this.material = RARITY_MATERIAL[content.rarity];
    this.rank = RARITY_RANK[content.rarity];
    const mat = rarityMaterial(this.material);
    this.nameText = content.name;

    const ramp = deriveRamp(mat.mid);
    const identityRamp = deriveRamp(content.colour);

    this.el = document.createElement("div");
    this.el.className = [
      "tc",
      `tc--${content.kind}`,
      `tc--${content.rarity}`,
    ].join(" ");
    this.el.setAttribute("aria-hidden", "true");
    this.el.style.setProperty("--tc-mid", mat.mid);
    this.el.style.setProperty("--tc-core", ramp.core);
    this.el.style.setProperty("--tc-rim", ramp.rim);
    this.el.style.setProperty("--tc-id", content.colour);
    this.el.style.setProperty("--tc-id-core", identityRamp.core);
    this.el.style.setProperty("--tc-scangap", `${UI_FOIL.scanlineGap}px`);

    // The lit frame canvas — behind the body, overscanned for the glow / foil.
    this.canvas = document.createElement("canvas");
    this.canvas.className = "tc-frame";
    this.canvas.setAttribute("aria-hidden", "true");
    this.ctx = this.canvas.getContext("2d");
    this.el.appendChild(this.canvas);

    // CSS foil overlays (cheap, reduced-motion-safe): scanlines, a scan-sweep,
    // and twinkling glints (the "being analysed" + holo-card read).
    const scan = document.createElement("div");
    scan.className = "tc-scan";
    scan.setAttribute("aria-hidden", "true");
    const sweep = document.createElement("div");
    sweep.className = "tc-sweep";
    sweep.setAttribute("aria-hidden", "true");
    const glints = document.createElement("div");
    glints.className = "tc-glints";
    glints.setAttribute("aria-hidden", "true");
    if (this.rank >= 1) this.seedGlints(glints, 8 + this.rank * 4);
    this.el.append(scan, sweep, glints);

    // The crisp DOM body — the readability layer.
    const body = document.createElement("div");
    body.className = "tc-body";
    body.innerHTML = this.buildBody(content);
    this.el.appendChild(body);

    this.artHost = body.querySelector(".tc-art") as HTMLDivElement;
    this.nameEl = body.querySelector(".tc-name") as HTMLDivElement;

    this.attachTilt();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObs = new ResizeObserver(() => this.drawFrame(1));
      this.resizeObs.observe(this.el);
    }
  }

  /** Mount the holo art (an `EnemyHolo` / `SkillEmblem` canvas) into the art window. */
  mountHolo(el: HTMLElement): void {
    this.artHost.appendChild(el);
  }

  /** The dramatic entrance: a slam-in + a name decode + the lit frame flare;
   *  legendary additionally sweeps its gold foil. Reduced-motion → static rich. */
  playReveal(): void {
    if (this.destroyed) return;
    const reduced = isReducedMotion();
    this.el.classList.remove("tc--revealing", "tc--dismissing");
    void this.el.offsetWidth; // reflow so the slam re-triggers
    if (!reduced) this.el.classList.add("tc--revealing");

    if (reduced) {
      this.nameEl.textContent = this.nameText;
      this.drawFrame(1);
      return;
    }
    this.decodeName();
    // The frame loop animates the legendary gold-foil sweep; lower tiers draw a
    // brief flare envelope then settle (no perpetual loop cost on a static frame).
    if (this.rarity === "legendary") this.frameLoop();
    else this.flareFrame();
  }

  /**
   * A one-shot, non-animated render: the name set instantly (no glyph-decode),
   * the lit frame drawn once at rest (no flare/legendary sweep loop). D-328 —
   * `playReveal()`'s slam/decode/frame-loop choreography (`tc--revealing`, the
   * `decodeName()` RAF loop, `flareFrame()`/`frameLoop()`) is tuned for exactly
   * ONE card on screen at a time (`EnemyReveal`'s single debut card) — it was
   * never built to run N-at-once, and doing so anyway (`EnemyLibrary`'s THREAT
   * ARCHIVE grid, pre-D-328) left every card but the one that won the shared
   * RAF/`getSharedUIBloom()` race each frame stuck mid-entrance (low opacity,
   * scaled/blurred, a scrambled name). Grid/list contexts that show MANY cards
   * at once should call this instead of `playReveal()`.
   */
  renderStatic(): void {
    if (this.destroyed) return;
    this.el.classList.remove("tc--revealing", "tc--dismissing");
    this.nameEl.textContent = this.nameText;
    this.drawFrame(1);
  }

  /** Slide / fade the card out (non-blocking). */
  dismiss(): void {
    if (this.destroyed) return;
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    if (isReducedMotion()) return;
    this.el.classList.remove("tc--revealing");
    this.el.classList.add("tc--dismissing");
  }

  destroy(): void {
    this.destroyed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.decodeRaf) cancelAnimationFrame(this.decodeRaf);
    this.raf = 0;
    this.decodeRaf = 0;
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.el.remove();
  }

  // ─── The frame material draw ─────────────────────────────────────────────────

  /** (Re)draw the lit rarity frame + circuit-etch + (legendary) gold-foil, scaled
   *  by `boost` (the reveal flare). `time` drives the moving gold catch-light.
   *  Null-ctx safe (headless → the CSS lit-edge rung carries it). */
  drawFrame(boost: number, time = 0): void {
    const ctx = this.ctx;
    if (!ctx || this.destroyed) return;
    const cw = this.el.clientWidth;
    const ch = this.el.clientHeight;
    if (cw <= 0 || ch <= 0) return;

    const dpr = Math.max(
      1,
      displayDpr(),
    );
    const W = cw + OVERSCAN * 2;
    const H = ch + OVERSCAN * 2;
    this.canvas.width = Math.round(W * dpr);
    this.canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const mat = rarityMaterial(this.material);
    const rect = { x: OVERSCAN, y: OVERSCAN, w: cw, h: ch };

    // 1 — the engraved lit frame (the bevelled edge + the §6.1 hot-edge fill).
    drawLitPanel(ctx, {
      rect,
      colour: mat.mid,
      state: "active",
      intensity: mat.intensity * boost,
      cornerRadius: 12,
      edgeRadius: 20,
      glow: this.rank >= 1,
    });

    // 2 — the refined circuit-etch corners (replaces the demoted ⌐ brackets).
    drawCircuitEtch(ctx, rect, mat.mid, {
      reach: UI_FOIL.etchReach,
      alpha: 0.55 + this.rank * 0.12,
      glow: this.rank >= 2,
    });

    // 3 — the LEGENDARY gold-foil specular sweep — a moving catch-light band
    // clipped to the frame (the "secret-rare you turn in the light"). Static
    // under reduced-motion (the gold frame + etch stay; just no travel).
    if (this.rarity === "legendary") {
      const ramp = deriveRamp(mat.mid);
      const span = W + H;
      const phase = isReducedMotion() ? 0.35 : (time / (UI_FOIL.foilSweepMs / 1000)) % 1;
      const pos = phase * span - H * 0.5;
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      const g = ctx.createLinearGradient(pos, 0, pos + 70, H);
      g.addColorStop(0, "transparent");
      g.addColorStop(0.5, ramp.core);
      g.addColorStop(1, "transparent");
      ctx.fillStyle = g;
      ctx.globalAlpha = 0.22 * Math.min(1.4, boost);
      ctx.fillRect(OVERSCAN, OVERSCAN, cw, ch);
      ctx.restore();
    }

    // 4 — route the hot frame through the ONE shared WebGL bloom.
    if (this.rank >= 1) {
      const bloom = getSharedUIBloom();
      if (bloom) {
        bloom.setDpr(dpr);
        const out = bloom.process(this.canvas);
        if (out) {
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.globalAlpha = Math.min(1, mat.bloomWeight + 0.25);
          ctx.drawImage(out, 0, 0, this.canvas.width, this.canvas.height);
          ctx.globalAlpha = 1;
        }
      }
    }
  }

  /** A one-shot material flare on reveal that decays to rest (non-legendary). */
  private flareFrame(): void {
    const t0 = now();
    const dur = UI_DURATION.settle * 1.4;
    const peak = 1 + (this.rank + 1) * 0.25;
    const tick = (): void => {
      if (this.destroyed) return;
      const t = Math.min(1, (now() - t0) / dur);
      const k = Math.sin(Math.min(1, t * 1.4) * Math.PI) * (1 - t);
      this.drawFrame(1 + (peak - 1) * Math.max(0, k));
      if (t < 1) this.raf = requestAnimationFrame(tick);
      else this.drawFrame(1);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** The legendary frame loop — animates the gold-foil specular sweep with a
   *  decaying entrance flare. ~30 fps throttle (transient + single card). */
  private frameLoop(): void {
    const t0 = now();
    let last = -1000;
    const tick = (): void => {
      if (this.destroyed || isReducedMotion()) return;
      const tnow = now();
      if (tnow - last >= 33) {
        last = tnow;
        const elapsed = tnow - t0;
        const k = Math.min(1, elapsed / (UI_DURATION.settle * 1.4));
        const boost = 1 + 0.8 * (1 - k); // flare → rest
        this.drawFrame(boost, elapsed / 1000);
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  // ─── Name decode (the cracked-terminal type-on) ──────────────────────────────

  private decodeName(): void {
    const el = this.nameEl;
    const final = this.nameText;
    if (!el || !final || isReducedMotion()) {
      if (el) el.textContent = final;
      return;
    }
    el.textContent = final; // robust seed if rAF never fires (headless)
    el.classList.add("tc-name--decoding");
    const glyphs = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789#%&/<>*";
    const t0 = now();
    const dur = 460;
    const tick = (): void => {
      if (this.destroyed) {
        el.textContent = final;
        return;
      }
      const t = Math.min(1, (now() - t0) / dur);
      const lock = Math.floor(t * final.length);
      let s = "";
      for (let i = 0; i < final.length; i++) {
        const c = final[i]!;
        if (i < lock || c === " ") s += c;
        else s += glyphs[Math.floor(Math.random() * glyphs.length)]!;
      }
      el.textContent = s;
      if (t < 1) this.decodeRaf = requestAnimationFrame(tick);
      else {
        el.textContent = final;
        el.classList.remove("tc-name--decoding");
      }
    };
    this.decodeRaf = requestAnimationFrame(tick);
  }

  // ─── Tilt (the BL-57 CSS-3D pivot) ───────────────────────────────────────────

  private attachTilt(): void {
    if (typeof window === "undefined") return;
    // Skill cards live inside the BL-60 `SkillCard` flip wrapper, which owns the
    // tilt (one pivot for the whole flip + the emblem parallax). The enemy
    // dossier keeps its own frame-level tilt here.
    if (this.kind === "skill") return;
    const handler = (e: MouseEvent): void => {
      if (this.destroyed || isReducedMotion()) return;
      const r = this.el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const nx = (e.clientX - r.left) / r.width - 0.5;
      const ny = (e.clientY - r.top) / r.height - 0.5;
      this.el.style.setProperty("--tc-tiltx", `${(-ny * 14).toFixed(2)}deg`);
      this.el.style.setProperty("--tc-tilty", `${(nx * 14).toFixed(2)}deg`);
      this.el.style.setProperty("--tc-parx", `${(nx * 14).toFixed(1)}px`);
      this.el.style.setProperty("--tc-pary", `${(ny * 14).toFixed(1)}px`);
    };
    const reset = (): void => {
      this.el.style.setProperty("--tc-tiltx", "0deg");
      this.el.style.setProperty("--tc-tilty", "0deg");
      this.el.style.setProperty("--tc-parx", "0px");
      this.el.style.setProperty("--tc-pary", "0px");
    };
    this.el.addEventListener("mousemove", handler);
    this.el.addEventListener("mouseleave", reset);
  }

  // ─── Body markup (the §9 slots) ──────────────────────────────────────────────

  private buildBody(c: TradingCardContent): string {
    return c.kind === "skill"
      ? this.buildSkillBody(c)
      : this.buildEnemyBody(c);
  }

  /** Render the rarity pips (filled diamonds), clamped 1..5. */
  private pipMarkup(c: TradingCardContent): string {
    const pips = Math.max(1, Math.min(5, c.pips ?? this.rank + 1));
    return Array.from({ length: pips }, () => `<i class="tc-pip"></i>`).join("");
  }

  /** The BL-60 skill = a compact **Trainer card** on the shared frame: the
   *  classification + the **type badge** in the banner, the emblem hero (mounted
   *  via `mountHolo`), the move-name + effect block, a persistence chip, and the
   *  collectible footer (dossier number + rarity pips + a flavour wink). Same
   *  identity as the enemy dossier, a card aspect instead of the tall column. */
  private buildSkillBody(c: TradingCardContent): string {
    const badge = c.typeBadge;
    const typeChip = badge
      ? `<span class="tc-type tc-type--${escapeHtml(badge.cat)}">${escapeHtml(
          badge.symbol,
        )} ${escapeHtml(badge.label)}</span>`
      : `<span class="tc-stamp-c">${escapeHtml(c.classBadge)}</span>`;

    const move = c.effect?.headline
      ? `<div class="tc-move">${escapeHtml(c.effect.headline)}</div>`
      : "";
    const fx = c.effect?.description
      ? `<div class="tc-fx">${escapeHtml(c.effect.description)}</div>`
      : "";
    const persist = c.persistence
      ? `<span class="tc-persist tc-persist--${escapeHtml(
          c.persistence.kind,
        )}">${escapeHtml(c.persistence.label)}</span>`
      : "";

    return `
      <div class="tc-stamp">
        <span class="tc-stamp-l">${escapeHtml(c.classification)}</span>
        ${typeChip}
      </div>
      <div class="tc-art tc-art--skill"></div>
      <div class="tc-codename">
        <div class="tc-name">${escapeHtml(c.name)}</div>
        <div class="tc-sub">${escapeHtml(c.subtitle)}</div>
      </div>
      <div class="tc-effect">
        ${move}
        ${fx}
        ${persist ? `<div class="tc-effect-foot">${persist}</div>` : ""}
      </div>
      <div class="tc-class">
        <span class="tc-foot-l">${escapeHtml(c.footerLeft ?? "")}</span>
        <span class="tc-pips">${this.pipMarkup(c)}</span>
      </div>
      ${c.flavour ? `<div class="tc-flavour">${escapeHtml(c.flavour)}</div>` : ""}`;
  }

  private buildEnemyBody(c: TradingCardContent): string {
    const pipHtml = this.pipMarkup(c);

    const stats = (c.stats ?? [])
      .map(
        (s) =>
          `<div class="tc-ln"><span class="tc-k">${escapeHtml(
            s.k,
          )}</span><span class="tc-v tc-v--${s.tone ?? "normal"}">${escapeHtml(
            s.v,
          )}</span></div>`,
      )
      .join("");

    // The matchup-row labels localize at render through the i18n seam (EN
    // fallback = the canonical text, so the EN card stays byte-identical); the
    // matchup VALUES are central element symbols passed in already-resolved.
    const matchups: string[] = [];
    if (c.weakness !== undefined)
      matchups.push(
        rowHtml(t("card.label.weakness", undefined, "WEAKNESS"), c.weakness, "weak"),
      );
    if (c.resistance !== undefined)
      matchups.push(
        rowHtml(t("card.label.resist", undefined, "RESIST"), c.resistance, "resist"),
      );
    if (c.immunities !== undefined)
      matchups.push(
        rowHtml(t("card.label.immune", undefined, "IMMUNE"), c.immunities, "immune"),
      );

    const ability = c.ability
      ? `<div class="tc-ability"><b>${escapeHtml(
          c.ability.title,
        )}</b><span>${escapeHtml(c.ability.desc)}</span></div>`
      : "";

    return `
      <div class="tc-stamp">
        <span class="tc-stamp-l">${escapeHtml(c.classification)}</span>
        <span class="tc-stamp-c">${escapeHtml(c.classBadge)}</span>
      </div>
      <div class="tc-art"></div>
      <div class="tc-codename">
        <div class="tc-name">${escapeHtml(c.name)}</div>
        <div class="tc-sub">${escapeHtml(c.subtitle)}</div>
      </div>
      <div class="tc-readout">
        ${stats}
        ${matchups.join("")}
        ${ability}
      </div>
      <div class="tc-class">
        <span class="tc-foot-l">${escapeHtml(c.footerLeft ?? "")}</span>
        <span class="tc-pips">${pipHtml}</span>
      </div>
      ${c.flavour ? `<div class="tc-flavour">${escapeHtml(c.flavour)}</div>` : ""}`;
  }

  /** Seed `n` twinkling glints (the holo-card sparkle). Render-only positions. */
  private seedGlints(host: HTMLDivElement, n: number): void {
    const cols = ["#ffffff", "#FFE9A8", "#9fd0ff", COLORS.SENTINEL_GREEN];
    for (let i = 0; i < n; i++) {
      const g = document.createElement("i");
      g.className = "tc-gl";
      g.style.left = `${Math.random() * 100}%`;
      g.style.top = `${Math.random() * 100}%`;
      g.style.color = cols[Math.floor(Math.random() * cols.length)]!;
      g.style.animationDelay = `${(Math.random() * 2.6).toFixed(2)}s`;
      host.appendChild(g);
    }
  }

  // ─── One injected stylesheet (built FROM the §9 tokens) ──────────────────────

  private static stylesInjected = false;

  static ensureStyles(): void {
    if (TradingCard.stylesInjected || typeof document === "undefined") return;
    TradingCard.stylesInjected = true;
    const T = UI_TYPE;
    const style = document.createElement("style");
    style.id = "trading-card-styles";
    style.textContent = `
      .tc {
        position: relative; overflow: hidden;
        display: flex; flex-direction: column;
        box-sizing: border-box;
        font-family: ${UI_FONT_STACK};
        color: ${T.body.color};
        background: linear-gradient(180deg,
          color-mix(in srgb, var(--tc-id, ${COLORS.ORACLE_RED}) 10%, ${COLORS.BG_PRIMARY}) 0%,
          ${COLORS.BG_PRIMARY} 60%, #060806 100%);
        pointer-events: auto;
        /* the CSS lit-edge rung (final fallback when no canvas paints). */
        border: 1px solid color-mix(in srgb, var(--tc-mid) 55%, transparent);
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--tc-core) 20%, transparent),
                    inset 0 -2px 14px rgba(0,0,0,0.6);
        transform: perspective(900px)
                   rotateX(var(--tc-tiltx, 0deg)) rotateY(var(--tc-tilty, 0deg));
        transition: transform ${UI_DURATION.hover}ms ${UI_EASE.settle};
      }
      /* enemy = the TALL vertical dossier filling the left HUD column. */
      .tc--enemy { width: 100%; height: 100%; border-radius: 10px; }
      /* skill = a card aspect (BL-60 lives in its own overlay). */
      .tc--skill { width: 300px; height: 418px; border-radius: 12px; }

      .tc-frame {
        position: absolute; inset: -${OVERSCAN}px;
        width: calc(100% + ${OVERSCAN * 2}px); height: calc(100% + ${OVERSCAN * 2}px);
        pointer-events: none; z-index: 0;
      }
      /* scanlines — the "being analysed" dossier texture. */
      .tc-scan {
        position: absolute; inset: 0; z-index: 4; pointer-events: none;
        mix-blend-mode: overlay; opacity: 0.5;
        background: repeating-linear-gradient(0deg,
          rgba(255,255,255,0.05) 0 1px, transparent 1px var(--tc-scangap, 3px));
      }
      /* a slow vertical scan-sweep (the malware-analysis pass). */
      .tc-sweep {
        position: absolute; left: 0; right: 0; height: 40%; z-index: 3;
        pointer-events: none; mix-blend-mode: screen; opacity: 0.4;
        background: linear-gradient(180deg, transparent,
          color-mix(in srgb, var(--tc-id-core, #9fd0ff) 40%, transparent) 60%,
          color-mix(in srgb, var(--tc-id-core, #9fd0ff) 55%, transparent), transparent);
        animation: tcSweep ${UI_FOIL.sweepMs}ms linear infinite;
      }
      @keyframes tcSweep { 0% { top: -45%; } 100% { top: 105%; } }
      /* twinkling glints (the holo-card sparkle field). */
      .tc-glints { position: absolute; inset: 0; z-index: 2; pointer-events: none; }
      .tc-gl {
        position: absolute; width: 2px; height: 2px; border-radius: 50%;
        background: currentColor; box-shadow: 0 0 5px 1px currentColor;
        animation: tcTwinkle 2.6s ease-in-out infinite;
      }
      @keyframes tcTwinkle {
        0%,100% { opacity: 0.12; transform: scale(0.6); }
        50% { opacity: 0.9; transform: scale(1.2); }
      }

      .tc-body {
        position: relative; z-index: 1;
        display: flex; flex-direction: column; flex: 1 1 auto;
        padding: ${UI_SPACE.xs}px; gap: ${UI_SPACE.hair}px; min-height: 0;
        /* BL-184 wrap guard on the card body (enemy 100% / skill 300px). */
        overflow-wrap: break-word; word-break: normal;
      }

      /* stamp — the classification banner (tier badge + manifest). */
      .tc-stamp {
        display: flex; justify-content: space-between; align-items: center;
        padding: ${UI_SPACE.hair}px ${UI_SPACE.xs}px;
        color: var(--tc-core, ${T.caption.color});
        border-bottom: 1px solid color-mix(in srgb, var(--tc-mid) 40%, transparent);
        background: linear-gradient(180deg, color-mix(in srgb, var(--tc-mid) 12%, transparent), transparent);
        letter-spacing: 1.5px;
      }
      .tc-stamp-l { font-size: ${roleFontSize("micro")}; font-weight: 700; }
      .tc-stamp-c {
        font-size: ${roleFontSize("micro")}; color: var(--tc-core);
        border: 1px solid color-mix(in srgb, var(--tc-mid) 60%, transparent);
        padding: 0 ${UI_SPACE.xs}px; border-radius: 3px;
        background: color-mix(in srgb, var(--tc-mid) 14%, #0a0a0a);
      }

      /* art window — the holo lives here (overscan canvas bleeds past it). */
      .tc-art {
        position: relative; flex: 1 1 auto; min-height: 84px;
        display: flex; align-items: center; justify-content: center;
        transform: translate(var(--tc-parx, 0px), var(--tc-pary, 0px));
        transition: transform ${UI_DURATION.hover}ms ${UI_EASE.settle};
      }
      .tc-holo-canvas {
        position: absolute; inset: -${OVERSCAN}px;
        width: calc(100% + ${OVERSCAN * 2}px); height: calc(100% + ${OVERSCAN * 2}px);
        pointer-events: none;
      }

      /* codename — the §9 display headline + a class/role subtitle. */
      .tc-codename { text-align: center; }
      .tc-name {
        font-size: ${withUiScale(T.display.size - 6)}; line-height: 1.0;
        letter-spacing: ${T.display.letterSpacing}px; text-transform: uppercase;
        color: ${COLORS.TEXT_GLITCH};
        text-shadow: 0 0 12px color-mix(in srgb, var(--tc-id) 70%, transparent), 0 2px 2px #000;
        white-space: nowrap;
      }
      .tc-name--decoding { color: ${COLORS.TEXT_GLITCH}; }
      .tc-sub {
        font-size: ${roleFontSize("micro")}; letter-spacing: 2px;
        color: var(--tc-core, ${T.caption.color}); text-transform: uppercase;
      }

      /* readout — the dossier list (leaded stat blocks) + the ability. */
      .tc-readout {
        display: flex; flex-direction: column; gap: 1px;
        margin-top: ${UI_SPACE.hair}px; font-size: ${T.body.size}px; min-height: 0;
      }
      .tc-ln {
        display: flex; justify-content: space-between;
        padding: 1px 2px; border-bottom: 1px dotted rgba(255,255,255,0.08);
      }
      .tc-k { color: ${T.caption.color}; letter-spacing: 1px; }
      .tc-v { color: ${T.body.color}; font-variant-numeric: tabular-nums; }
      .tc-v--hot { color: ${COLORS.ORACLE_RED}; }
      .tc-v--gold { color: ${RARITY_GOLD}; }
      .tc-v--weak { color: ${COLORS.AMBER_WARN}; }
      .tc-v--resist { color: ${COLORS.ICE_CYAN}; }
      .tc-v--immune { color: var(--tc-core, ${T.caption.color}); }
      .tc-ability {
        margin-top: ${UI_SPACE.xxs}px;
        background: rgba(255,255,255,0.03);
        border-left: 2px solid var(--tc-mid);
        padding: ${UI_SPACE.xxs}px ${UI_SPACE.xs}px; border-radius: 0 5px 5px 0;
      }
      .tc-ability b {
        display: block; color: var(--tc-core); letter-spacing: 1px;
        font-size: ${T.caption.size}px;
      }
      .tc-ability span {
        color: ${T.caption.color}; font-size: ${roleFontSize("micro")}; line-height: 1.35;
      }

      /* footer — faction sigil + dossier number + rarity pips. */
      .tc-class {
        display: flex; justify-content: space-between; align-items: center;
        margin-top: auto; padding-top: ${UI_SPACE.xxs}px;
        border-top: 1px solid color-mix(in srgb, var(--tc-mid) 30%, transparent);
        color: var(--tc-core, ${T.caption.color}); letter-spacing: 1.5px;
        font-size: ${roleFontSize("micro")};
      }
      .tc-pips { display: flex; gap: 3px; }
      .tc-pip {
        width: 7px; height: 7px; transform: rotate(45deg);
        background: var(--tc-mid); box-shadow: 0 0 5px var(--tc-mid);
      }
      .tc-flavour {
        font-size: ${roleFontSize("micro")}; font-style: italic; text-align: center;
        color: ${T.caption.color}; opacity: 0.7; margin-top: ${UI_SPACE.hair}px;
      }

      /* ── legendary: the gold-foil read (data-gold, NOT ornate) ── */
      .tc--legendary {
        box-shadow: inset 0 1px 0 color-mix(in srgb, ${RARITY_GOLD} 30%, transparent),
                    inset 0 -2px 14px rgba(0,0,0,0.6),
                    0 0 22px color-mix(in srgb, ${RARITY_GOLD} 35%, transparent);
      }
      .tc--legendary .tc-name {
        text-shadow: 0 0 14px color-mix(in srgb, ${RARITY_GOLD} 65%, transparent),
                     0 0 2px ${RARITY_GOLD}, 0 2px 2px #000;
      }

      /* ── secret: the cloaked read ── */
      .tc--secret .tc-name { color: #cdd6e4; }

      /* ════ BL-60 skill aspect — the Trainer card on the shared frame ════
         A compact card aspect (not the tall dossier): banner + type badge +
         emblem hero + effect block + collectible footer. The host (.skillcard)
         sizes the box; the frame fills it.

         CARD-FIT (BL-60 polish): the skill type scale is **size-aware** via three
         INHERITED custom props the host can set so a small card (the swap deck)
         renders complete, untruncated text instead of full-size type that clips:
           --tcs            the type scale multiplier (default 1 → byte-identical
                            to the full draft/[S] card; floors keep small text legible)
           --tc-skill-art-h the art-window height (default 116px → unchanged)
           --tc-skill-name-ws the name white-space (default nowrap → unchanged)
         Floors via max(floorpx, calc(basepx * var(--tcs,1))) guarantee every line
         stays readable however small the card; default 1 leaves every existing
         surface unchanged (the floor is always <= the base). */
      .skillcard .tc--skill { width: 100%; height: 100%; }
      .tc--skill .tc-body { padding: ${UI_SPACE.xs}px; gap: ${UI_SPACE.hair}px; }

      /* the Trainer "type" badge — Supporter/Item/Tool/Stadium → our
         Offensive/Defensive/Utility/Legendary. Reads as the card's TYPE. */
      .tc-type {
        font-size: ${cardFloor(T.caption.size - 3)};
        letter-spacing: 1px;
        padding: 1px ${UI_SPACE.xs}px; border-radius: 3px; white-space: nowrap;
        color: var(--tc-core);
        border: 1px solid color-mix(in srgb, var(--tc-mid) 60%, transparent);
        background: color-mix(in srgb, var(--tc-mid) 14%, #0a0a0a);
        text-transform: uppercase;
      }
      .tc--skill .tc-stamp-l { font-size: ${cardFloor(T.caption.size - 2)}; }

      /* the emblem hero — the SHOWCASE. It overlaps the banner edge for 3D pop,
         but the art window is a FIXED, capped height so the emblem stays a
         contained hero and never grows over the title (with MAX_EMBLEM_R). The
         height is host-tunable (--tc-skill-art-h) so a small card can give the
         text more room and keep the emblem a small hero. */
      /* BL-296 — the art window is the card's SLACK SINK. The copy compression
         (D-348) shortened every effect text, and the footer's own margin-top:auto
         collected the whole difference into ONE dead band above it: measured
         228.5 dp — 36.3 % of the card — on a card whose headline says it all and
         needs no description. The window now GROWS into that slack up to
         --tc-skill-art-max-h (default: no growth, so every compact surface is
         unchanged) and the emblem grows with it, so a short-copy card reads as a
         bigger hero instead of a hole. */
      .tc--skill .tc-art {
        flex: 1 1 auto; height: var(--tc-skill-art-h, 116px);
        /* BL-298 — the hero may also give a little back when the copy is at its
           DE worst case, down to a host-set floor. Default = the base height, so
           it still cannot shrink anywhere the host has not opted in. */
        min-height: var(--tc-skill-art-min-h, var(--tc-skill-art-h, 116px));
        max-height: var(--tc-skill-art-max-h, var(--tc-skill-art-h, 116px));
        margin: -2px 0 0; overflow: hidden;
      }
      /* whatever slack the art cap leaves is SPLIT above and below the text
         stack (the footer keeps its own margin-top:auto), so the card reads as
         three composed bands — hero, text, footer — at every copy length. */
      .tc--skill .tc-codename { margin-top: auto; }
      .tc--skill .tc-name {
        font-size: ${cardFloor(T.title.size - 4)};
        line-height: 1.1; white-space: var(--tc-skill-name-ws, normal);
      }
      .tc--skill .tc-sub {
        font-size: ${cardFloor(T.caption.size - 2)};
        letter-spacing: 1.5px;
      }
      .tc--skill .tc-class { font-size: ${cardFloor(T.caption.size - 2)}; }
      .tc--skill .tc-flavour { font-size: ${cardFloor(T.caption.size - 3)}; }

      /* the effect block — the treated move-name + leaded effect text + chip. */
      .tc-effect {
        display: flex; flex-direction: column; gap: 1px;
        background: rgba(255,255,255,0.03);
        border-left: 2px solid var(--tc-mid);
        padding: ${UI_SPACE.xxs}px ${UI_SPACE.xs}px; border-radius: 0 5px 5px 0;
        margin-top: ${UI_SPACE.hair}px;
      }
      .tc-move {
        color: var(--tc-core); letter-spacing: 1px; text-transform: uppercase;
        font-size: ${cardFloor(T.body.size - 1)};
        text-shadow: 0 0 8px color-mix(in srgb, var(--tc-mid) 55%, transparent);
      }
      .tc-fx {
        color: ${COLORS.TEXT_PRIMARY};
        font-size: ${cardFloor(T.label.size)};
        line-height: 1.34;
      }
      .tc-effect-foot {
        display: flex; align-items: center; gap: ${UI_SPACE.xs}px; margin-top: 2px;
      }
      .tc-persist {
        font-size: ${cardFloor(T.caption.size - 4)};
        padding: 1px ${UI_SPACE.xs}px;
        letter-spacing: 1px; border: 1px solid rgba(140,190,140,0.45);
        color: #9AC59A; background: rgba(12,26,12,0.6);
      }
      .tc-persist--instant { color: ${COLORS.AMBER_WARN}; border-color: rgba(232, 208, 152,0.55); background: rgba(50,38,6,0.65); }
      .tc-persist--payload { color: ${COLORS.AMBER_WARN}; border-color: rgba(232, 208, 152,0.8); background: rgba(60,44,0,0.85); }
      .tc-persist--duration { color: #FFB884; border-color: rgba(255,164,96,0.5); background: rgba(42,24,10,0.65); }
      .tc-persist--permanent { color: #88F2B8; border-color: rgba(136,242,184,0.5); background: rgba(8,32,20,0.65); }

      /* entrance: anticipate → snap slam. */
      .tc--revealing {
        animation: tcSlam ${UI_DURATION.settle * 1.8}ms ${UI_EASE.snap} both;
      }
      @keyframes tcSlam {
        0%   { opacity: 0; transform: perspective(900px) scale(0.7) translateY(10px); filter: brightness(1.7); }
        12%  { transform: perspective(900px) scale(0.62) translateY(12px); }
        60%  { opacity: 1; transform: perspective(900px) scale(1.04) translateY(-2px); filter: brightness(1.2); }
        100% { opacity: 1; transform: perspective(900px) scale(1) translateY(0); filter: brightness(1); }
      }
      .tc--dismissing {
        animation: tcDismiss ${UI_DURATION.settle}ms ${UI_EASE.decay} both;
      }
      @keyframes tcDismiss {
        0%   { opacity: 1; transform: translateY(0) scale(1); }
        100% { opacity: 0; transform: translateY(-12px) scale(0.96); }
      }

      @media (prefers-reduced-motion: reduce) {
        .tc { transition: none; --tc-tiltx: 0deg; --tc-tilty: 0deg; }
        .tc-sweep, .tc-gl { animation: none; }
        .tc-sweep { opacity: 0.2; top: 30%; }
        .tc--revealing, .tc--dismissing { animation: none; }
      }
    `;
    document.head.appendChild(style);
  }
}

/** The 12px base type floor (BL-192) — the §9 `micro` role size; no card text
 *  computes below it at `--ui-scale:1` / `--tcs:1`. */
const CARD_FLOOR_PX = 12;

/**
 * BL-192 — the single shared card type-floor helper. The skill aspect scales its
 * text DOWN via `--tcs` (the swap deck / contender shrink it) and `--ui-scale`,
 * so each font-size is `max(<floor>, calc(<base> * --tcs * --ui-scale))`. Before
 * BL-192 the floors were scattered sub-12px literals (`max(7px…)`, `max(7.5px…)`,
 * `max(8px…)`, `max(11px…)`), so dense cards could render text below the 12px
 * base legibility floor. This clamps EVERY card font at the 12px `micro` floor —
 * one helper, one floor — trading a little flavour density for guaranteed
 * legibility (the audit §6.2 / BL-192 call). At `--ui-scale:1` / `--tcs:1` the
 * render is unchanged wherever the base already ≥ 12px.
 */
function cardFloor(basePx: number): string {
  return `max(${CARD_FLOOR_PX}px, calc(${basePx}px * var(--tcs, 1) * var(--ui-scale, 1)))`;
}

function rowHtml(key: string, value: string, tone: string): string {
  return `<div class="tc-ln"><span class="tc-k">${escapeHtml(
    key,
  )}</span><span class="tc-v tc-v--${tone}">${escapeHtml(value)}</span></div>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Test/reset hook — drops the injected style guard. */
export function __resetTradingCardForTest(): void {
  TradingCard["stylesInjected"] = false;
}
